import React, { useState, useEffect } from 'react';
import { FiAlertCircle, FiCalendar, FiClock, FiInfo } from 'react-icons/fi';
import { eventsAPI } from '../services/api';
import { Event, EventType } from '../types';
import { formatGpsDate, getDateFromGpsOrIso } from '../utils/dateUtils';
import { sanitizeDeviceText } from '../utils/deviceDisplay.ts';

interface DeviceEventsProps {
    deviceId: string;
}

const DeviceEvents: React.FC<DeviceEventsProps> = ({ deviceId }) => {
    const [events, setEvents] = useState<Event[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchDeviceEvents = async () => {
            if (!deviceId) {
                setError('Device ID is missing');
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setError(null);
                const data = await eventsAPI.getEventsByDeviceId(deviceId);
                setEvents(data);
            } catch (err: any) {
                console.error('Error fetching device events:', err);

                // Check if it's a 404 error (no events found)
                if (err.response?.status === 404) {
                    // No events is a normal state, not an error
                    setEvents([]);
                    setError(null);
                } else {
                    // Only show error for actual errors, not for "no events"
                    setError('Failed to fetch device events: ' + (err.message || 'Unknown error'));
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchDeviceEvents();
    }, [deviceId]);

    // Helper to get event type display name and color
    const getEventInfo = (eventType: EventType | string): { name: string; bgColor: string; textColor: string; icon: React.ReactNode } => {
        switch (eventType) {
            case EventType.LOAD_UP:
                return {
                    name: "Load Up",
                    bgColor: "bg-blue-100",
                    textColor: "text-blue-800",
                    icon: <FiInfo className="h-5 w-5 text-blue-600" />
                };
            case EventType.GRID_EMERGENCY:
                return {
                    name: "Grid Emergency",
                    bgColor: "bg-red-100",
                    textColor: "text-red-800",
                    icon: <FiAlertCircle className="h-5 w-5 text-red-600" />
                };
            case EventType.CRITICAL_PEAK:
                return {
                    name: "Critical Peak",
                    bgColor: "bg-orange-100",
                    textColor: "text-orange-800",
                    icon: <FiAlertCircle className="h-5 w-5 text-orange-600" />
                };
            case EventType.START_SHED:
                return {
                    name: "Start Shed",
                    bgColor: "bg-amber-100",
                    textColor: "text-amber-800",
                    icon: <FiClock className="h-5 w-5 text-amber-600" />
                };
            case EventType.END_SHED:
                return {
                    name: "End Shed",
                    bgColor: "bg-green-100",
                    textColor: "text-green-800",
                    icon: <FiClock className="h-5 w-5 text-green-600" />
                };
            case EventType.INFO_REQUEST:
                return {
                    name: "Information Request",
                    bgColor: "bg-purple-100",
                    textColor: "text-purple-800",
                    icon: <FiInfo className="h-5 w-5 text-purple-600" />
                };
            default:
                const rawType = sanitizeDeviceText(eventType) || 'unreported';
                return {
                    name: `Unknown Event (${rawType})`,
                    bgColor: "bg-gray-100",
                    textColor: "text-gray-800",
                    icon: <FiInfo className="h-5 w-5 text-gray-600" />
                };
        }
    };

    const formatEventDate = (dateValue?: string | number): string => {
        const formatted = formatGpsDate(dateValue);
        return formatted === 'Invalid Date' ? 'Unknown' : formatted;
    };

    const getAcknowledgementLabel = (eventAck?: boolean): string => {
        if (eventAck === true) return 'Acknowledged';
        if (eventAck === false) return 'Pending';
        return 'Unknown acknowledgement';
    };

    const getAcknowledgementClass = (eventAck?: boolean): string => {
        if (eventAck === true) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
        if (eventAck === false) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200';
        return 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200';
    };

    // Sort events by date in descending order (most recent first)
    const sortedEvents = [...events].sort((a, b) => {
        // Choose the appropriate date field based on the event type
        const getDateString = (event: Event) => {
            if (event.event_type === EventType.INFO_REQUEST && event.event_data?.timestamp) {
                return event.event_data.timestamp;
            }
            return event.event_data?.start_time || "0";
        };

        const dateA = getDateFromGpsOrIso(getDateString(a)) || new Date(0);
        const dateB = getDateFromGpsOrIso(getDateString(b)) || new Date(0);
        return dateB.getTime() - dateA.getTime();
    });

    return (
        <div className="bg-card shadow overflow-hidden sm:rounded-lg mb-8">
            <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-card-foreground">Device Events</h3>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Current and past events for this device.</p>
            </div>

            {isLoading ? (
                <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                </div>
            ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-600 p-4 mb-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <FiAlertCircle className="h-5 w-5 text-red-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    </div>
                </div>
            ) : sortedEvents.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                    <FiCalendar className="h-12 w-12 mx-auto text-muted-foreground" />
                    <p className="mt-2">No events found for this device.</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-border">
                        <thead className="bg-muted">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Event Type
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Start Time
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Duration
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-card divide-y divide-border">
                            {sortedEvents.map((event) => {
                                const eventInfo = getEventInfo(event.event_type);
                                // Get the appropriate date field based on event type
                                const dateField = event.event_type === EventType.INFO_REQUEST
                                    ? event.event_data?.timestamp
                                    : event.event_data?.start_time;
                                const eventId = sanitizeDeviceText(event.event_id) || 'Unknown event ID';

                                return (
                                    <tr key={eventId}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0">
                                                    {eventInfo.icon}
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-card-foreground">
                                                        {eventInfo.name}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {eventId}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-card-foreground">{formatEventDate(dateField)}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-card-foreground">
                                                {event.event_type === EventType.INFO_REQUEST
                                                    ? 'N/A'
                                                    : (event.event_data?.duration
                                                        ? `${event.event_data.duration} seconds`
                                                        : 'N/A')}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getAcknowledgementClass(event.event_ack)}`}>
                                                {getAcknowledgementLabel(event.event_ack)}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default DeviceEvents;
